import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { AnimatedCounter } from "../components/AnimatedCounter";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Filter, 
  Home,
  ArrowRight,
  Upload,
  Trash2,
  Zap,
  Target,
  Clock,
  AlertTriangle,
  TrendingUp,
  Building2,
  Shield,
  Radio,
  Globe,
  EyeOff
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Property photo mapping by type
const PROPERTY_PHOTOS = {
  sfh: "https://images.pexels.com/photos/4469150/pexels-photo-4469150.jpeg?auto=compress&cs=tinysrgb&w=800",
  mf: "https://images.unsplash.com/photo-1664833189203-cbd564954b11?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  multifamily: "https://images.unsplash.com/photo-1664833189203-cbd564954b11?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  condo: "https://images.pexels.com/photos/5563473/pexels-photo-5563473.jpeg?auto=compress&cs=tinysrgb&w=800",
  commercial: "https://images.pexels.com/photos/36237028/pexels-photo-36237028.jpeg?auto=compress&cs=tinysrgb&w=800",
  land: "https://images.unsplash.com/photo-1716077521105-f0d699ab2047?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  default: "https://images.pexels.com/photos/4469150/pexels-photo-4469150.jpeg?auto=compress&cs=tinysrgb&w=800",
};

function getPropertyPhoto(type) {
  if (!type) return PROPERTY_PHOTOS.default;
  const t = type.toLowerCase();
  if (t === "sfh" || t.includes("single")) return PROPERTY_PHOTOS.sfh;
  if (t === "mf" || t.includes("multi") || t.includes("apartment")) return PROPERTY_PHOTOS.mf;
  if (t.includes("condo")) return PROPERTY_PHOTOS.condo;
  if (t.includes("commercial") || t.includes("retail")) return PROPERTY_PHOTOS.commercial;
  if (t.includes("land") || t.includes("lot")) return PROPERTY_PHOTOS.land;
  return PROPERTY_PHOTOS.default;
}

// Execution stages with colors
const STAGE_CONFIG = {
  new_lead: { label: 'NEW LEAD', color: '#3B82F6' },
  analyzed: { label: 'ANALYZED', color: '#6366F1' },
  underwritten: { label: 'UNDERWRITTEN', color: '#8B5CF6' },
  contracted: { label: 'CONTRACTED', color: '#D4AF37' },
  closed: { label: 'CLOSED', color: '#10B981' },
  dead: { label: 'DEAD', color: '#EF4444' }
};

// Quick next action calculator for deal cards
const getQuickNextAction = (deal) => {
  const stage = deal.pipeline_stage;
  
  if (stage === "new_lead") {
    if (!deal.asking_price) return "Add asking price";
    return "Run analysis";
  }
  if (stage === "analyzed") {
    if (!deal.rehab_estimate) return "Estimate rehab costs";
    return "Move to underwriting";
  }
  if (stage === "underwritten") {
    return "Generate contract";
  }
  if (stage === "contracted") {
    return "Get contractor bids";
  }
  if (stage === "closed") {
    return "Completed";
  }
  if (stage === "dead") {
    return "Archived";
  }
  return "Review deal";
};

export default function DealsPage({ user, subscription, onLogout }) {
  const navigate = useNavigate();
  const { mainClass } = useSidebar();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showNewDealModal, setShowNewDealModal] = useState(false);
  const [newDeal, setNewDeal] = useState({
    property_address: "",
    property_type: "SFH",
    beds: "",
    baths: "",
    sqft: "",
    owner_name: "",
    owner_phone: "",
    owner_email: "",
    lead_source: "",
    parcel_id: "",
    county: "",
    legal_description: "",
    asking_price: ""
  });

  const propertyTypes = [
    { value: "SFH", label: "Single Family Home" },
    { value: "MF", label: "Multi Family" },
    { value: "Condo", label: "Condo" },
    { value: "Land", label: "Land" },
    { value: "Commercial", label: "Commercial" }
  ];

  const stages = [
    { value: "all", label: "All Stages" },
    { value: "new_lead", label: "New Lead" },
    { value: "analyzed", label: "Analyzed" },
    { value: "underwritten", label: "Underwritten" },
    { value: "contracted", label: "Contracted" },
    { value: "closed", label: "Closed" },
    { value: "dead", label: "Dead" }
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchDeals();
  }, [stageFilter, searchTerm]);

  const fetchDeals = async () => {
    try {
      let url = `${BACKEND_URL}/api/deals?`;
      if (stageFilter !== "all") url += `stage=${stageFilter}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setDeals(response.data);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
      toast.error("Failed to load deals");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeal = async (e) => {
    e.preventDefault();
    
    if (!newDeal.property_address.trim()) {
      toast.error("Property address is required");
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/deals`,
        {
          ...newDeal,
          beds: newDeal.beds ? parseInt(newDeal.beds) : null,
          baths: newDeal.baths ? parseFloat(newDeal.baths) : null,
          sqft: newDeal.sqft ? parseInt(newDeal.sqft) : null,
          asking_price: newDeal.asking_price ? parseFloat(newDeal.asking_price) : null,
          lead_source: newDeal.lead_source || null,
          parcel_id: newDeal.parcel_id || null,
          county: newDeal.county || null,
          legal_description: newDeal.legal_description || null
        },
        { withCredentials: true }
      );
      
      toast.success("Transaction created successfully");
      setShowNewDealModal(false);
      setNewDeal({
        property_address: "",
        property_type: "SFH",
        beds: "",
        baths: "",
        sqft: "",
        owner_name: "",
        owner_phone: "",
        owner_email: "",
        lead_source: "",
        parcel_id: "",
        county: "",
        legal_description: "",
        asking_price: ""
      });
      navigate(`/deals/${response.data.deal_id}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create transaction";
      toast.error(message);
    }
  };

  const handleDeleteDeal = async (dealId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm("Are you sure you want to delete this transaction?")) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/deals/${dealId}`, { withCredentials: true });
      toast.success("Transaction deleted");
      fetchDeals();
    } catch (error) {
      toast.error("Failed to delete transaction");
    }
  };

  const handleToggleNetwork = async (dealId, currentStatus, e) => {
    e.preventDefault();
    e.stopPropagation();
    const newStatus = currentStatus === "live" ? "hidden" : "live";
    try {
      await axios.put(`${BACKEND_URL}/api/deals/${dealId}/marketplace-status`,
        { status: newStatus }, { withCredentials: true });
      setDeals(prev => prev.map(d => d.deal_id === dealId ? { ...d, marketplace_status: newStatus } : d));
      if (newStatus === "live") {
        toast.success("Deal is now LIVE on the Network!");
      } else {
        toast.success("Deal removed from network");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update network status");
    }
  };

  // Calculate metrics
  const totalCapital = deals.reduce((sum, d) => sum + (d.asking_price || 0), 0);
  const activeDeals = deals.filter(d => !['closed', 'dead'].includes(d.pipeline_stage)).length;
  const contractedDeals = deals.filter(d => d.pipeline_stage === 'contracted').length;
  const avgScore = deals.length > 0 
    ? Math.round(deals.reduce((sum, d) => sum + (d.priority_score || 50), 0) / deals.length)
    : 0;

  // Get stage counts
  const stageCounts = deals.reduce((acc, deal) => {
    acc[deal.pipeline_stage] = (acc[deal.pipeline_stage] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-black text-white">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass} data-testid="deals-page">
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-black/80">
          <div className="px-4 md:px-6 py-3 md:py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Left: Branding + Status */}
              <div className="flex items-center gap-4 md:gap-6">
                <div>
                  <h1 className="text-base md:text-lg font-semibold tracking-tight">
                    Active <span className="text-[#D4AF37]">Transactions</span>
                  </h1>
                  <p className="text-[9px] text-white/50 uppercase tracking-[0.15em] hidden md:block">Track and manage every deal from lead to close across your pipeline.</p>
                </div>
                
                {/* Live Badge */}
                <div className="flex items-center gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                  <div className="relative w-2 h-2">
                    <div className="absolute inset-0 bg-emerald-400 rounded-full" />
                    <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
                  </div>
                  <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Live</span>
                </div>
              </div>

              {/* Center: Key Metrics - Hidden on mobile */}
              <div className="hidden lg:flex items-center gap-8">
                <MetricPill label="Total Capital" value={`$${(totalCapital / 1000000).toFixed(2)}M`} />
                <div className="w-px h-10 bg-white/[0.04]" />
                <MetricPill label="Active" value={activeDeals} highlight />
                <div className="w-px h-10 bg-white/[0.04]" />
                <MetricPill label="Contracted" value={contractedDeals} />
                <div className="w-px h-10 bg-white/[0.04]" />
                <MetricPill label="Avg Score" value={avgScore} />
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-4">
                <Dialog open={showNewDealModal} onOpenChange={setShowNewDealModal}>
                  <DialogTrigger asChild>
                    <button 
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold text-sm rounded-xl hover:opacity-90 transition-opacity"
                      data-testid="add-deal-btn"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">New Transaction</span>
                      <span className="sm:hidden">New</span>
                    </button>
                  </DialogTrigger>
                  <NewDealDialog 
                    newDeal={newDeal}
                    setNewDeal={setNewDeal}
                    propertyTypes={propertyTypes}
                    onSubmit={handleCreateDeal}
                    onClose={() => setShowNewDealModal(false)}
                  />
                </Dialog>
                
                <div className="text-right hidden md:block">
                  <div className="font-mono text-lg text-white/70 tracking-tight">
                    {currentTime.toLocaleTimeString('en-US', { hour12: false })}
                  </div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">Eastern</div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Stage Flow Strip */}
        <section className="border-b border-white/[0.06] bg-black">
          <div className="px-4 md:px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Pipeline Distribution</h2>
              <span className="text-[10px] text-white/50 font-mono">{deals.length} Total</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(STAGE_CONFIG).map(([key, config]) => (
                <StageCard 
                  key={key}
                  stage={key}
                  config={config}
                  count={stageCounts[key] || 0}
                  isActive={stageFilter === key}
                  onClick={() => setStageFilter(stageFilter === key ? 'all' : key)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Filters + Content */}
        <div className="p-4 md:p-6">
          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/[0.02] border border-white/[0.06] text-white text-sm placeholder:text-white/40 focus:border-[#D4AF37]/30 focus:outline-none rounded-xl transition-all duration-300"
                placeholder="Search by address or owner..."
                data-testid="search-deals"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-full sm:w-48 bg-white/[0.02] border-white/[0.06] text-white h-10 rounded-xl">
                  <Filter className="w-4 h-4 mr-2 text-white/50" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/[0.1]">
                  {stages.map(stage => (
                    <SelectItem key={stage.value} value={stage.value} className="text-white hover:bg-white/[0.04]">
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Link to="/deals?import=true" className="hidden sm:block">
                <button className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.02] border border-white/[0.06] text-white/70 hover:text-white hover:border-white/[0.15] text-sm rounded-xl transition-all">
                  <Upload className="w-4 h-4" />
                  Import
                </button>
              </Link>
            </div>
          </div>

          {/* Deals Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin shadow-[0_0_15px_rgba(212,175,55,0.3)]" />
            </div>
          ) : deals.length === 0 ? (
            <div className="text-center py-16 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl">
              <Building2 className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Transactions Found</h3>
              <p className="text-sm text-white/50 mb-6">
                {searchTerm || stageFilter !== "all" 
                  ? "Try adjusting your search or filters"
                  : "Add your first transaction to begin capital deployment"}
              </p>
              {!searchTerm && stageFilter === "all" && (
                <button 
                  onClick={() => setShowNewDealModal(true)} 
                  className="px-6 py-2.5 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold rounded-sm hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  New Transaction
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {deals.map((deal, idx) => (
                <TransactionCard 
                  key={deal.deal_id}
                  deal={deal}
                  index={idx}
                  onDelete={handleDeleteDeal}
                  onToggleNetwork={handleToggleNetwork}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// Components

function MetricPill({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[9px] font-medium text-white/50 uppercase tracking-[0.12em] mb-1">{label}</div>
      <span className={`text-xl font-light tracking-tight ${highlight ? 'text-[#D4AF37]' : 'text-white'}`}>
        {value}
      </span>
    </div>
  );
}

function StageCard({ stage, config, count, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`bg-[#0A0A0A]/80 backdrop-blur-sm border rounded-2xl p-3 text-left transition-all duration-400 hover:-translate-y-1 ${
        isActive 
          ? 'border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] shadow-[0_0_20px_rgba(212,175,55,0.1)]' 
          : 'border-white/[0.06] hover:border-white/[0.15] hover:shadow-[0_15px_30px_rgba(0,0,0,0.3)]'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span 
          className="px-2 py-0.5 rounded text-[8px] font-semibold uppercase tracking-wider"
          style={{ color: config.color, background: `${config.color}15` }}
        >
          {config.label}
        </span>
        <AnimatedCounter value={count} className="text-lg font-light text-white" duration={1000} />
      </div>
    </button>
  );
}

function TransactionCard({ deal, index, onDelete, onToggleNetwork }) {
  const stageConfig = STAGE_CONFIG[deal.pipeline_stage] || STAGE_CONFIG.new_lead;
  const isActive = ['contracted', 'closing'].includes(deal.pipeline_stage);
  const spread = (deal.arv || 0) - (deal.asking_price || 0) - (deal.rehab_estimate || 0);
  const spreadPositive = spread > 0;
  const isLive = deal.marketplace_status === "live";
  const photo = deal.photos?.[0]
    ? (deal.photos[0].startsWith('http') ? deal.photos[0] : `${BACKEND_URL}${deal.photos[0]}`)
    : getPropertyPhoto(deal.property_type);

  return (
    <Link
      to={`/deals/${deal.deal_id}`}
      className={`group relative bg-[#0A0A0A]/80 backdrop-blur-sm border rounded-2xl overflow-hidden cursor-pointer transition-all duration-400 hover:-translate-y-2 animate-fade-in ${
        isActive
          ? 'border-[#D4AF37]/30 shadow-[0_0_30px_rgba(212,175,55,0.1)] hover:shadow-[0_0_50px_rgba(212,175,55,0.2)] hover:border-[#D4AF37]/50'
          : 'border-white/[0.06] hover:border-white/[0.15] hover:shadow-[0_25px_50px_rgba(0,0,0,0.4)]'
      }`}
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`deal-card-${deal.deal_id}`}
    >
      {/* Hero Photo */}
      <div className="relative h-40 overflow-hidden">
        <img src={photo} alt={deal.property_address}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/10 to-transparent" />
        {/* Stage badge */}
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wider backdrop-blur-sm"
            style={{ color: stageConfig.color, background: `${stageConfig.color}25`, border: `1px solid ${stageConfig.color}40` }}>
            {stageConfig.label}
          </span>
        </div>
        {/* Priority + pulse */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {deal.priority_score >= 70 && (
            <span className="px-2 py-0.5 bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-lg text-[9px] font-bold text-[#00FF88] backdrop-blur-sm">HIGH</span>
          )}
          {isActive && (
            <div className="relative w-2.5 h-2.5">
              <div className="absolute inset-0 bg-[#D4AF37] rounded-full shadow-[0_0_10px_rgba(212,175,55,0.8)]" />
              <div className="absolute inset-0 bg-[#D4AF37] rounded-full animate-ping opacity-50" />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Address */}
        <h3 className="relative text-base text-white mb-1 truncate font-semibold group-hover:text-white/90 transition-all duration-300">{deal.property_address}</h3>
        <div className="relative flex items-center gap-2 text-[11px] text-white/40 mb-5">
          <span>{deal.property_type}</span>
          {deal.beds && <span>• {deal.beds} bed</span>}
          {deal.baths && <span>• {deal.baths} bath</span>}
          {deal.sqft && <span>• {deal.sqft.toLocaleString()} sqft</span>}
        </div>

        {/* Metrics Grid */}
        <div className="relative grid grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-[9px] text-white/40 uppercase tracking-widest mb-1.5">Asking</div>
            <div className="text-xl font-mono font-bold text-white">
              ${deal.asking_price ? (deal.asking_price / 1000).toFixed(0) + 'K' : '—'}
            </div>
          </div>
          <div>
            <div className="text-[9px] text-white/40 uppercase tracking-widest mb-1.5">Spread</div>
            <div className={`text-xl font-mono font-bold ${
              spreadPositive
                ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]'
                : 'text-white/30'
            }`}>
              {spreadPositive ? `+$${(spread / 1000).toFixed(0)}K` : '—'}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative pt-3 border-t border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-3">
            {deal.pipeline_stage !== "closed" && deal.pipeline_stage !== "dead" && (
              <div className="flex items-center gap-1.5 text-[10px] text-[#D4AF37]">
                <Zap className="w-3 h-3" />
                <span className="font-semibold">{getQuickNextAction(deal)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Post to Network toggle */}
            <button
              onClick={(e) => onToggleNetwork(deal.deal_id, deal.marketplace_status, e)}
              data-testid={`network-toggle-${deal.deal_id}`}
              title={isLive ? "Remove from Network" : "Post to Network"}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border transition-all duration-200 ${
                isLive
                  ? "text-[#00FF88] bg-emerald-500/10 border-emerald-500/30 shadow-[0_0_8px_rgba(0,255,136,0.15)]"
                  : "text-white/30 bg-white/[0.03] border-white/[0.08] hover:text-white/60 hover:border-white/20"
              }`}>
              {isLive
                ? <><Radio className="w-2.5 h-2.5" /> LIVE</>
                : <><Globe className="w-2.5 h-2.5" /> Post</>
              }
            </button>
            <button
              onClick={(e) => onDelete(deal.deal_id, e)}
              className="p-1.5 text-white/30 hover:text-[#FF3B30] transition-all duration-300"
              data-testid={`delete-deal-${deal.deal_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function NewDealDialog({ newDeal, setNewDeal, propertyTypes, onSubmit, onClose }) {
  return (
    <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.1] max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-lg font-semibold text-white">New Transaction</DialogTitle>
      </DialogHeader>
      
      <form onSubmit={onSubmit} className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label className="text-[10px] text-white/50 uppercase tracking-wider">Property Address *</Label>
          <Input
            value={newDeal.property_address}
            onChange={(e) => setNewDeal({...newDeal, property_address: e.target.value})}
            className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
            placeholder="123 Main St, City, State ZIP"
            required
            data-testid="new-deal-address"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[10px] text-white/50 uppercase tracking-wider">Property Type</Label>
            <Select 
              value={newDeal.property_type} 
              onValueChange={(v) => setNewDeal({...newDeal, property_type: v})}
            >
              <SelectTrigger className="bg-white/[0.02] border-white/[0.06] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/[0.1]">
                {propertyTypes.map(type => (
                  <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/[0.02] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)]">
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label className="text-[10px] text-white/50 uppercase tracking-wider">Beds</Label>
            <Input
              type="number"
              value={newDeal.beds}
              onChange={(e) => setNewDeal({...newDeal, beds: e.target.value})}
              className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
              placeholder="3"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[10px] text-white/50 uppercase tracking-wider">Baths</Label>
            <Input
              type="number"
              step="0.5"
              value={newDeal.baths}
              onChange={(e) => setNewDeal({...newDeal, baths: e.target.value})}
              className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
              placeholder="2"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-[10px] text-white/50 uppercase tracking-wider">Sqft</Label>
            <Input
              type="number"
              value={newDeal.sqft}
              onChange={(e) => setNewDeal({...newDeal, sqft: e.target.value})}
              className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
              placeholder="1500"
            />
          </div>
        </div>
        
        <div className="border-t border-white/[0.06] pt-4">
          <h3 className="text-[10px] font-semibold text-white/70 uppercase tracking-wider mb-3">Property Details</h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] text-white/50 uppercase tracking-wider">Asking Price ($)</Label>
                <Input
                  type="number"
                  value={newDeal.asking_price}
                  onChange={(e) => setNewDeal({...newDeal, asking_price: e.target.value})}
                  className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                  placeholder="250000"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[10px] text-white/50 uppercase tracking-wider">Lead Source</Label>
                <Input
                  value={newDeal.lead_source}
                  onChange={(e) => setNewDeal({...newDeal, lead_source: e.target.value})}
                  className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                  placeholder="Direct Mail, Cold Call, etc."
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] text-white/50 uppercase tracking-wider">Parcel ID / APN</Label>
                <Input
                  value={newDeal.parcel_id}
                  onChange={(e) => setNewDeal({...newDeal, parcel_id: e.target.value})}
                  className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                  placeholder="123-456-789"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[10px] text-white/50 uppercase tracking-wider">County</Label>
                <Input
                  value={newDeal.county}
                  onChange={(e) => setNewDeal({...newDeal, county: e.target.value})}
                  className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                  placeholder="Travis County"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[10px] text-white/50 uppercase tracking-wider">Legal Description</Label>
              <Input
                value={newDeal.legal_description}
                onChange={(e) => setNewDeal({...newDeal, legal_description: e.target.value})}
                className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                placeholder="Lot 5, Block 3, Oak Hills Subdivision"
              />
            </div>
          </div>
        </div>
        
        <div className="border-t border-white/[0.06] pt-4">
          <h3 className="text-[10px] font-semibold text-white/70 uppercase tracking-wider mb-3">Owner Info</h3>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[10px] text-white/50 uppercase tracking-wider">Owner Name</Label>
              <Input
                value={newDeal.owner_name}
                onChange={(e) => setNewDeal({...newDeal, owner_name: e.target.value})}
                className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                placeholder="John Smith"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] text-white/50 uppercase tracking-wider">Phone</Label>
                <Input
                  type="tel"
                  value={newDeal.owner_phone}
                  onChange={(e) => setNewDeal({...newDeal, owner_phone: e.target.value})}
                  className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[10px] text-white/50 uppercase tracking-wider">Email</Label>
                <Input
                  type="email"
                  value={newDeal.owner_email}
                  onChange={(e) => setNewDeal({...newDeal, owner_email: e.target.value})}
                  className="bg-white/[0.02] border-white/[0.06] text-white focus:border-[#D4AF37]/30"
                  placeholder="owner@email.com"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 pt-4">
          <button 
            type="button" 
            onClick={onClose}
            className="px-4 py-2 bg-white/[0.02] border border-white/[0.06] text-white/70 hover:text-white rounded-sm transition-all duration-300"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="px-4 py-2 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold rounded-sm hover:opacity-90 transition-opacity"
            data-testid="create-deal-btn"
          >
            Create Transaction
          </button>
        </div>
      </form>
    </DialogContent>
  );
}
